import { auth, graphqlClient } from "../graphqlClient";

const STORAGE_KEY = "admin_panel_settings_v1";

export const DEFAULT_ADMIN_SETTINGS = {
  platformName: "PayGate Thailand",
  timezone: "Asia/Bangkok",
  defaultCurrency: "THB",
  requireIPWhitelist: true,
  hmacAlgorithm: "sha256",
  signatureHeaderName: "X-Signature",
  timestampTolerance: 300,
  callbackRetryEnabled: true,
  callbackMaxRetries: 5,
  callbackRetryDelay: [5, 30, 120, 600, 3600],
  callbackTimeout: 30,
  webhookIPValidation: true,
  webhookSignatureValidation: true,
  webhookDuplicateCheck: true,
  qrDefaultExpiry: 15,
  qrMaxAmount: 2000000,
  qrMinAmount: 20,
  routingHealthCheckInterval: 60,
  routingFailoverThreshold: 3,
  routingAutoDisableOnFail: true,
  emailNotifications: true,
  slackNotifications: false,
  alertThreshold: 10000,
};

const mergeWithDefaults = (raw) => {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_ADMIN_SETTINGS };

  return {
    ...DEFAULT_ADMIN_SETTINGS,
    ...raw,
    callbackRetryDelay: Array.isArray(raw.callbackRetryDelay)
      ? raw.callbackRetryDelay.filter((n) => Number.isFinite(n) && n >= 0)
      : DEFAULT_ADMIN_SETTINGS.callbackRetryDelay,
  };
};

const getAdminSettingsFromBackend = async () => {
  const query = `
    query AdminPanelSettings {
      adminPanelSettings {
        platformName
        timezone
        defaultCurrency
        requireIPWhitelist
        hmacAlgorithm
        signatureHeaderName
        timestampTolerance
        callbackRetryEnabled
        callbackMaxRetries
        callbackRetryDelay
        callbackTimeout
        webhookIPValidation
        webhookSignatureValidation
        webhookDuplicateCheck
        qrDefaultExpiry
        qrMaxAmount
        qrMinAmount
        routingHealthCheckInterval
        routingFailoverThreshold
        routingAutoDisableOnFail
        emailNotifications
        slackNotifications
        alertThreshold
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result?.adminPanelSettings || null;
};

const updateAdminSettingsOnBackend = async (settings) => {
  const mutation = `
    mutation UpdateAdminPanelSettings($input: AdminPanelSettingsInput!) {
      updateAdminPanelSettings(input: $input) {
        platformName
        timezone
        defaultCurrency
        requireIPWhitelist
        hmacAlgorithm
        signatureHeaderName
        timestampTolerance
        callbackRetryEnabled
        callbackMaxRetries
        callbackRetryDelay
        callbackTimeout
        webhookIPValidation
        webhookSignatureValidation
        webhookDuplicateCheck
        qrDefaultExpiry
        qrMaxAmount
        qrMinAmount
        routingHealthCheckInterval
        routingFailoverThreshold
        routingAutoDisableOnFail
        emailNotifications
        slackNotifications
        alertThreshold
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input: settings });
  return result?.updateAdminPanelSettings || null;
};

export const getAdminSettings = async () => {
  try {
    // Try backend first (if schema supports it), otherwise fall back to local storage.
    try {
      const remote = await getAdminSettingsFromBackend();
      if (remote) {
        const mergedRemote = mergeWithDefaults(remote);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedRemote));
        return mergedRemote;
      }
    } catch {
      // Ignore backend capability errors and use local settings.
    }

    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return { ...DEFAULT_ADMIN_SETTINGS };
    const parsed = JSON.parse(saved);
    return mergeWithDefaults(parsed);
  } catch {
    return { ...DEFAULT_ADMIN_SETTINGS };
  }
};

export const updateAdminSettings = async (settings) => {
  const normalized = mergeWithDefaults(settings);

  try {
    const remote = await updateAdminSettingsOnBackend(normalized);
    if (remote) {
      const mergedRemote = mergeWithDefaults(remote);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedRemote));
      return { ...mergedRemote, _savedToBackend: true };
    }
  } catch {
    // Fallback to local persistence when backend mutation isn't available.
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  return { ...normalized, _savedToBackend: false };
};

export const resetAdminSettings = async () => {
  const mutation = `
    mutation ResetAdminPanelSettings {
      resetAdminPanelSettings {
        platformName
        timezone
        defaultCurrency
        requireIPWhitelist
        hmacAlgorithm
        signatureHeaderName
        timestampTolerance
        callbackRetryEnabled
        callbackMaxRetries
        callbackRetryDelay
        callbackTimeout
        webhookIPValidation
        webhookSignatureValidation
        webhookDuplicateCheck
        qrDefaultExpiry
        qrMaxAmount
        qrMinAmount
        routingHealthCheckInterval
        routingFailoverThreshold
        routingAutoDisableOnFail
        emailNotifications
        slackNotifications
        alertThreshold
      }
    }
  `;

  try {
    const result = await graphqlClient.mutate(mutation);
    const remote = result?.resetAdminPanelSettings || null;

    if (remote) {
      const mergedRemote = mergeWithDefaults(remote);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedRemote));
      return mergedRemote;
    }
  } catch {
    // Fall back to local defaults if the backend reset is unavailable.
  }

  localStorage.removeItem(STORAGE_KEY);
  return { ...DEFAULT_ADMIN_SETTINGS };
};

export const getAdminRuntimeData = async () => {
  const me = await auth.me();

  const enginesQuery = `
    query GetAdminEnginesForSettings {
      getAllEngines {
        depositFee
        withdrawalFee
        commissionRate
      }
    }
  `;

  const enginesResult = await graphqlClient.query(enginesQuery);
  const engines = enginesResult?.getAllEngines || [];

  const numeric = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const count = engines.length || 1;
  const avgDepositFee = engines.reduce((sum, e) => sum + numeric(e.depositFee), 0) / count;
  const avgWithdrawalFee = engines.reduce((sum, e) => sum + numeric(e.withdrawalFee), 0) / count;
  const avgCommissionRate = engines.reduce((sum, e) => sum + numeric(e.commissionRate), 0) / count;

  return {
    profile: {
      name: me?.name || "Admin",
      email: me?.email || "-",
      status: me?.status || "ACTIVE",
    },
    feeSnapshot: {
      avgDepositFee,
      avgWithdrawalFee,
      avgCommissionRate,
      sampledEngines: engines.length,
    },
  };
};

export const regenerateAdminSessionTokens = async () => {
  const role = graphqlClient.getUserRole();
  if (!role || role.toLowerCase() !== "admin") {
    throw new Error("Session token regeneration is only available for admin users");
  }

  const refreshed = await graphqlClient.refreshAccessToken();
  if (!refreshed) {
    throw new Error("Unable to regenerate session tokens");
  }

  return true;
};
